/**
 * Apply calculation rules directly to form state (plain object).
 * Used by FormV2 instead of ruleEngineMixin (which works on DOM).
 * Rule format: same as API - action (pattern) and targetField (destination).
 * Field IDs use hyphens: "group_expenses-total_expenses" → path "group_expenses.total_expenses".
 */

import { getValueByPath, setValueByPath } from "./useFormEngine";
import { getCalculationDecimalPlaces, roundNumberBySettings } from "@/utils/numberFormatter";

/**
 * Convert rule field ID to dot path for state.
 * "group_expenses-total_expenses" → "group_expenses.total_expenses"
 */
export function ruleIdToPath(id) {
  if (!id || typeof id !== "string") return "";
  return id.split("-").join(".");
}

/**
 * Recursively collect all leaf values when path contains one or more *.
 * e.g. "group_invoice.productandprice.*.products.*.unit_qty" → [qty1, qty2, ...]
 */
function getValuesByPathWithWildcards(state, path) {
  const parts = path.split(".").filter(Boolean);
  function collect(obj, partIdx) {
    if (partIdx >= parts.length) return [obj];
    const key = parts[partIdx];
    if (key === "*") {
      if (!Array.isArray(obj)) return [];
      let result = [];
      for (let i = 0; i < obj.length; i++) {
        result = result.concat(collect(obj[i], partIdx + 1));
      }
      return result;
    }
    const next = obj?.[key];
    return collect(next, partIdx + 1);
  }
  return collect(state, 0);
}

/**
 * Get concrete paths when path contains * (for per-item set). Returns full paths.
 * e.g. "group_invoice.productandprice.*.products.*.unit_totalprice" → ["group_invoice.productandprice.0.products.0.unit_totalprice", ...]
 */
function getConcretePaths(state, pathWithStars) {
  const parts = pathWithStars.split(".");
  const result = [];
  function traverse(obj, partIdx, pathSoFar) {
    if (partIdx >= parts.length) {
      result.push(pathSoFar);
      return;
    }
    const key = parts[partIdx];
    if (key === "*") {
      if (!Array.isArray(obj)) return;
      for (let i = 0; i < obj.length; i++) {
        const nextPath = pathSoFar ? `${pathSoFar}.${i}` : String(i);
        traverse(obj[i], partIdx + 1, nextPath);
      }
      return;
    }
    const next = obj?.[key];
    const nextPath = pathSoFar ? `${pathSoFar}.${key}` : key;
    traverse(next, partIdx + 1, nextPath);
  }
  traverse(state, 0, "");
  return result;
}

/**
 * Get value from state at path. Supports wildcard * for array sum.
 * e.g. "group_expenses.expenses.*.amount" → sum of group_expenses.expenses[i].amount
 */
export function getValueFromState(state, pathOrRuleId) {
  const path = pathOrRuleId.includes("-") && !pathOrRuleId.includes(".") ? ruleIdToPath(pathOrRuleId) : pathOrRuleId;
  if (!path.includes("*")) {
    return getValueByPath(state, path);
  }
  return getValuesByPathWithWildcards(state, path);
}

/**
 * Evaluate numeric expression: replace field IDs (hyphen form) with values from state, then evaluate.
 * Supports +, -, *, /, (), and X as multiplication. Field IDs become values via getValueFromState.
 */
export function evaluateExpressionOnState(expression, state, decimalPlaces) {
  const places =
    decimalPlaces !== undefined && decimalPlaces !== null
      ? decimalPlaces
      : getCalculationDecimalPlaces();
  if (!expression || typeof expression !== "string") return 0;
  let processed = expression.replace(/\s*X\s*/g, "*");
  const fieldMatches = processed.match(/[a-zA-Z][a-zA-Z0-9_\-]*/g) || [];
  const fieldMap = new Map();
  fieldMatches.forEach((fieldId) => {
    if (!isNaN(fieldId)) return;
    const path = ruleIdToPath(fieldId);
    const val = getValueByPath(state, path);
    const num = parseFloat(val) ?? 0;
    fieldMap.set(fieldId, num);
  });
  let evaluated = processed;
  const sorted = Array.from(fieldMap.keys()).sort((a, b) => b.length - a.length);
  sorted.forEach((fieldId) => {
    const value = fieldMap.get(fieldId);
    const re = new RegExp(`\\b${fieldId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
    evaluated = evaluated.replace(re, value);
  });
  try {
    if (!/^[0-9+\-*/().\s]+$/.test(evaluated)) return 0;
    const evaluateSimple = (expr) => {
      const parts = expr.split(/([+\-])/).filter((p) => p.trim());
      let result = 0;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();
        if (part === "+" || part === "-") continue;
        const multDiv = part.split(/([*\/])/).filter((p) => p.trim());
        let partResult = parseFloat(multDiv[0]) || 0;
        for (let j = 1; j < multDiv.length; j += 2) {
          const op = multDiv[j];
          const operand = parseFloat(multDiv[j + 1]) || 0;
          if (op === "*") partResult *= operand;
          else if (op === "/") partResult = operand !== 0 ? partResult / operand : partResult;
        }
        if (i === 0) result = partResult;
        else result = parts[i - 1] === "+" ? result + partResult : result - partResult;
      }
      return result;
    };
    const evaluateFull = (expr) => {
      while (expr.includes("(")) {
        expr = expr.replace(/\(([^()]+)\)/g, (_, inner) => evaluateSimple(inner));
      }
      return evaluateSimple(expr);
    };
    return roundNumberBySettings(evaluateFull(evaluated), places);
  } catch (e) {
    return 0;
  }
}

/**
 * Evaluate a condition string against state using get(path).
 * condition format: "fieldId operator value" e.g. "group_details-marine_ins === true"
 * Supports ===, !==, ==, !=, >, <, >=, <=. Booleans: true/false/on/off.
 */
export function evaluateConditionInState(condition, get) {
  if (!condition || typeof condition !== "string") return true;
  const parts = condition.trim().split(/\s+/);
  if (parts.length < 3) return true;
  const fieldId = parts[0];
  const operator = parts[1];
  const valueStr = parts.slice(2).join(" ").trim();
  const path = ruleIdToPath(fieldId);
  const rawValue = get(path);
  console.log("[FormCalc] evaluateConditionInState", { condition, path, rawValue });
  const normalize = (v) => {
    if (v === true || v === false) return v;
    if (v === "true" || v === "on" || v === 1) return true;
    if (v === "false" || v === "off" || v === 0) return false;
    if (v === undefined || v === null || v === "") return false; // no definido → false en comparaciones booleanas
    if (typeof v === "string" && !isNaN(v)) return parseFloat(v);
    if (typeof v === "number") return v;
    return v;
  };
  const comparisonValue = normalize(valueStr);
  const processedFieldValue = (typeof comparisonValue === "boolean")
    ? normalize(rawValue) // comparación booleana: undefined/null/'' → false
    : (rawValue === undefined || rawValue === null ? rawValue : normalize(rawValue));

  switch (operator) {
    case "===": return processedFieldValue === comparisonValue;
    case "!==": return processedFieldValue !== comparisonValue;
    case "==": return processedFieldValue == comparisonValue;
    case "!=": return processedFieldValue != comparisonValue;
    case ">": return processedFieldValue > comparisonValue;
    case "<": return processedFieldValue < comparisonValue;
    case ">=": return processedFieldValue >= comparisonValue;
    case "<=": return processedFieldValue <= comparisonValue;
    default: return false;
  }
}

/**
 * Apply a single calculation rule to state. Mutates state.
 * rule: { pattern (action), destination (targetField), condition?, validationMessage? }
 * multiRuleTargetPaths: optional Set of paths (e.g. "group.x.field") that are written by more than one rule; when condition fails we do not set 0 for these so the other rule can set the value (avoids overwriting).
 */
export function applyRuleToState(state, rule, getValue, setValue, multiRuleTargetPaths = null, decimalPlaces = null) {
  const places =
    decimalPlaces !== undefined && decimalPlaces !== null
      ? decimalPlaces
      : getCalculationDecimalPlaces();
  const pattern = rule.pattern ?? rule.action ?? "";
  const destination = rule.destination ?? rule.targetField ?? "";
  const destPath = ruleIdToPath(destination);
  const isGroupPath = destPath.includes(".");
  if (isGroupPath) {
    console.log("[FormCalc] regla con destination en group (un nivel)", { pattern, destination, destPath, fullRule: rule });
  }
  if (!destination) return;

  const get = getValue || ((path) => getValueByPath(state, path));
  const set = setValue || ((path, value) => setValueByPath(state, path, value));

  const conditionHasN = rule.condition && rule.condition.includes("-N-");
  const isMultiRuleTarget = multiRuleTargetPaths && (multiRuleTargetPaths.has(destPath) || multiRuleTargetPaths.has(destination));

  // Condition: when false, clear destination so dependent rules recalculate. If this target is written by multiple rules (e.g. one === true, another !== true), do not set 0 so the other rule's value is not overwritten.
  if (rule.condition && !conditionHasN) {
    if (!evaluateConditionInState(rule.condition, get)) {
      if (!isMultiRuleTarget) set(destPath, 0);
      return;
    }
  }

  if (pattern.includes("+") && !pattern.includes(" - ")) {
    const fields = pattern.split("+").map((f) => f.trim()).filter(Boolean);
    let total = 0;
    const values = [];
    fields.forEach((fieldId) => {
      const path = ruleIdToPath(fieldId);
      const raw = get(path);
      const v = parseFloat(raw) || 0;
      if (path.includes(".")) {
        console.log("[FormCalc] suma campo en group", { fieldId, path, raw, parsed: v });
      }
      values.push(v);
      total += v;
    });
    const result = roundNumberBySettings(total, places);
    if (destPath.includes(".")) {
      console.log("[FormCalc] suma → set destination (group)", { destPath, result, values });
    }
    set(destPath, result);
    return;
  }

  if (pattern.includes(" - ")) {
    const [minuend, subtrahend] = pattern.split(" - ").map((f) => f.trim());
    const pathA = ruleIdToPath(minuend);
    const pathB = ruleIdToPath(subtrahend);
    const a = parseFloat(get(pathA)) || 0;
    const b = parseFloat(get(pathB)) || 0;
    const result = roundNumberBySettings(a - b, places);
    if (destPath.includes(".") || pathA.includes(".") || pathB.includes(".")) {
      console.log("[FormCalc] resta (group)", { destPath, minuend, subtrahend, pathA, pathB, a, b, result });
    }
    set(destPath, result);
    return;
  }

  // Copy per row: pattern and destination both have -N-, no operators → copy source to destination for each index
  const hasN = pattern.includes("-N-") || destination.includes("-N-");
  const noOperators = !pattern.includes("+") && !pattern.includes(" - ") && !pattern.includes(" X ") && !pattern.includes("(") && !pattern.includes("/");
  if (hasN && noOperators && pattern.includes("-N-") && destination.includes("-N-")) {
    const destPathConverted = ruleIdToPath(destination);
    if (destPathConverted.includes(".N.")) {
      const destParts = destPathConverted.split(".");
      const nIdx = destParts.findIndex((p) => p === "N");
      if (nIdx >= 0) {
        const arrayPath = destParts.slice(0, nIdx).join(".");
        const arr = get(arrayPath);
        if (Array.isArray(arr)) {
          for (let i = 0; i < arr.length; i++) {
            const destForI = destination.replace(/-N-/g, `-${i}-`);
            if (conditionHasN && rule.condition) {
              const conditionForI = rule.condition.replace(/-N-/g, `-${i}-`);
              if (!evaluateConditionInState(conditionForI, get)) {
                if (!isMultiRuleTarget) set(ruleIdToPath(destForI), 0);
                continue;
              }
            }
            const patternForI = pattern.replace(/-N-/g, `-${i}-`);
            const srcPath = ruleIdToPath(patternForI);
            const value = get(srcPath);
            set(ruleIdToPath(destForI), value !== undefined && value !== null ? value : 0);
          }
          return;
        }
      }
    }
  }

  // Per-item multiplication: "path1 X path2" → destination (each with same * structure, e.g. *-products-*-unit_totalprice)
  if (pattern.includes(" X ") && pattern.split("-").includes("*")) {
    const [pattern1, pattern2] = pattern.split(" X ").map((p) => p.trim());
    const path1 = ruleIdToPath(pattern1);
    const path2 = ruleIdToPath(pattern2);
    const destPathConverted = ruleIdToPath(destination);
    if (path1.includes("*") && path2.includes("*") && destPathConverted.includes("*")) {
      const concretePaths = getConcretePaths(state, destPathConverted);
      const basePaths = concretePaths.map((p) => p.split(".").slice(0, -1).join("."));
      const field1 = path1.split(".").pop();
      const field2 = path2.split(".").pop();
      const destField = destPathConverted.split(".").pop();
      const values = [];
      basePaths.forEach((basePath) => {
        const v1 = parseFloat(get(`${basePath}.${field1}`)) || 0;
        const v2 = parseFloat(get(`${basePath}.${field2}`)) || 0;
        const product = roundNumberBySettings(v1 * v2, places);
        set(`${basePath}.${destField}`, product);
        values.push(product);
      });
      return;
    }
  }

  if (pattern.includes("*") && pattern.split("-").includes("*") && !pattern.includes(" X ")) {
    const path = ruleIdToPath(pattern);
    const destPathConverted = ruleIdToPath(destination);
    const patternStars = (path.match(/\*/g) || []).length;
    const destStars = (destPathConverted.match(/\*/g) || []).length;

    if (destStars >= 1 && patternStars > destStars) {
      const destConcrete = getConcretePaths(state, destPathConverted);
      const patternParts = path.split(".");
      const firstStarIdx = patternParts.findIndex((p) => p === "*");
      const prefix = patternParts.slice(0, firstStarIdx).join(".");
      const restAfterFirstStar = patternParts.slice(firstStarIdx + 1).join(".");
      const destField = destPathConverted.split(".").pop();
      destConcrete.forEach((fullPath) => {
        const basePath = fullPath.split(".").slice(0, -1).join(".");
        if (!basePath.startsWith(prefix)) return;
        const indexPart = basePath.substring(prefix.length + 1).split(".")[0];
        const patternPathForIndex = `${prefix}.${indexPart}.${restAfterFirstStar}`;
        const values = getValuesByPathWithWildcards(state, patternPathForIndex);
        const total = roundNumberBySettings(values.reduce((sum, v) => sum + (parseFloat(v) || 0), 0), places);
        set(`${basePath}.${destField}`, total);
      });
      return;
    }

    if (destStars >= 1 && patternStars === destStars) {
      const destConcrete = getConcretePaths(state, destPathConverted);
      const patternParts = path.split(".");
      const firstStarIdx = patternParts.findIndex((p) => p === "*");
      const prefix = patternParts.slice(0, firstStarIdx).join(".");
      const restAfterFirstStar = patternParts.slice(firstStarIdx + 1).join(".");
      const destField = destPathConverted.split(".").pop();
      const values = [];
      destConcrete.forEach((fullPath) => {
        const basePath = fullPath.split(".").slice(0, -1).join(".");
        if (!basePath.startsWith(prefix)) return;
        const indexPart = basePath.substring(prefix.length + 1).split(".")[0];
        const patternPathForIndex = `${prefix}.${indexPart}.${restAfterFirstStar}`;
        const v = parseFloat(get(patternPathForIndex)) || 0;
        set(`${basePath}.${destField}`, roundNumberBySettings(v, places));
        values.push(v);
      });
      return;
    }

    const values = getValuesByPathWithWildcards(state, path);
    const numeric = values.map((v) => parseFloat(v) || 0);
    const total = roundNumberBySettings(numeric.reduce((sum, v) => sum + v, 0), places);
    set(destPath, total);
    return;
  }

  if (pattern.includes("X") || pattern.includes("/") || pattern.includes("(")) {
    const destPathConverted = ruleIdToPath(destination);
    const hasN = pattern.includes("-N-") || destination.includes("-N-");
    if (hasN && destPathConverted.includes(".N.")) {
      const destParts = destPathConverted.split(".");
      const nIdx = destParts.findIndex((p) => p === "N");
      if (nIdx >= 0) {
        const arrayPath = destParts.slice(0, nIdx).join(".");
        const arr = get(arrayPath);
        if (Array.isArray(arr)) {
          const values = [];
          for (let i = 0; i < arr.length; i++) {
            const destForI = destination.replace(/-N-/g, `-${i}-`);
            if (conditionHasN && rule.condition) {
              const conditionForI = rule.condition.replace(/-N-/g, `-${i}-`);
              if (!evaluateConditionInState(conditionForI, get)) {
                if (!isMultiRuleTarget) set(ruleIdToPath(destForI), 0);
                continue;
              }
            }
            const patternForI = pattern.replace(/-N-/g, `-${i}-`);
            const result = evaluateExpressionOnState(patternForI, state, places);
            set(ruleIdToPath(destForI), result);
            values.push(result);
          }
          return;
        }
      }
    }
    const result = evaluateExpressionOnState(pattern, state, places);
    if (destPath.includes(".")) {
      console.log("[FormCalc] expresión → set destination (group)", { pattern, destPath, result });
    }
    set(destPath, result);
    return;
  }

  if (pattern.includes("*")) {
    const path = ruleIdToPath(pattern);
    const values = getValuesByPathWithWildcards(state, path);
    const numeric = values.map((v) => parseFloat(v) || 0);
    const total = roundNumberBySettings(numeric.reduce((sum, v) => sum + v, 0), places);
    set(destPath, total);
  }
}

/**
 * Apply all calculation rules to state. Runs in dependency-friendly order (multiple passes).
 */
export function applyCalculationRules(state, rules, getValue, setValue, options = {}) {
  const decimalPlaces =
    options.decimalPlaces !== undefined && options.decimalPlaces !== null
      ? options.decimalPlaces
      : getCalculationDecimalPlaces();
  const rulesCount = rules?.length ?? 0;
  const ruleDests = rules?.map((r) => r.destination ?? r.targetField) ?? [];
  console.log("[FormCalc] applyCalculationRules", { rulesCount, ruleDests, stateKeys: state ? Object.keys(state) : [], decimalPlaces });
  if (!state || !Array.isArray(rules) || rules.length === 0) return;
  const get = getValue || ((path) => getValueByPath(state, path));
  const set = setValue || ((path, value) => setValueByPath(state, path, value));
  const activeRules = rules.filter((r) => r.isActive !== false);
  const destCount = new Map();
  activeRules.forEach((r) => {
    const d = r.destination ?? r.targetField ?? "";
    if (d) destCount.set(d, (destCount.get(d) || 0) + 1);
  });
  const multiRuleTargetPaths = new Set();
  destCount.forEach((count, dest) => {
    if (count > 1) {
      multiRuleTargetPaths.add(ruleIdToPath(dest));
      multiRuleTargetPaths.add(dest);
    }
  });
  const maxPasses = 5;
  for (let pass = 0; pass < maxPasses; pass++) {
    activeRules.forEach((rule) => {
      try {
        applyRuleToState(state, rule, get, set, multiRuleTargetPaths, decimalPlaces);
      } catch (e) {
        console.error("[FormCalc] applyRule ERROR", { rule, error: e.message });
      }
    });
  }
}
